import { CSSProperties, JSX, useEffect, useRef, useState } from "react";
import { HeaderContext, SortDirection } from "@tanstack/react-table";
import { StyledHeadCell } from "baseui/table-grid";
import { colors, css, fonts, getGraphColor, glyphs } from "../../../Styles";
import { IdentifiableRow } from "../reactiveTableRenderer";
import { convertPxValueToNumber, getFrozenColumnStyle, getWidthIfPinned, pinnedWidthOffset } from "../_Tables/getTableStyles";
import { TableStyles_Types } from "../../../Definitions";
import SecondaryPopover from "../../BaseComponents/secondaryPopover";

interface HeadCellProps {
  style: CSSProperties,
  cellValue: string|JSX.Element,
  onClick?: (event: unknown) => void;
  onHoverContent?: JSX.Element;
  isSorted?: false | SortDirection;
  keyPass: string;
  colorId?: number;
  sortArrow?: boolean
  pinningStyle: TableStyles_Types
}

export const HeadCell = <T extends IdentifiableRow,>({style, cellValue, onClick, onHoverContent, keyPass, isSorted, colorId, column, table, sortArrow, pinningStyle}: HeaderContext<T, unknown> & HeadCellProps) => {
  const [isHovered, setIsHovered] = useState<boolean>(false); 
  const referenceRef = useRef<HTMLDivElement | null>(null);
  
  const value = colorId !== undefined && !onHoverContent
    ? <div style={{position: "relative", cursor: isHovered ? "pointer" : "cursor"}} onClick={onClick} onMouseEnter={() => ((onClick&&setIsHovered) || setIsHovered)(true)} onMouseLeave={() => ((onClick&&setIsHovered) || setIsHovered)(false)}>
        <div style={{backgroundColor: getGraphColor(colorId), ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary, transition: "filter 0.2s ease", filter: isHovered ? "opacity(0.3)" : "opacity(1)"}}>
          {cellValue}
        </div> 
      {isHovered && onClick ? <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}><glyphs.Delete /></div> : <></>}
    </div>
    
    : colorId !== undefined && onHoverContent ?
      <div ref={referenceRef} style={{position: "relative"}} onClick={onClick} onMouseEnter={() => ((onClick&&setIsHovered) || setIsHovered)(true)} onMouseLeave={() => ((onClick&&setIsHovered) || setIsHovered)(false)}>
        <SecondaryPopover
          content={ <div style={{ backgroundColor: getGraphColor(colorId), ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary }}>
            {cellValue}
          </div> }
          referenceRef={referenceRef}
          isHovered={isHovered}
          popoverContent={onHoverContent}
          popoverHeader={cellValue as string}
        />
      </div>
      
    : cellValue;
    
  const columnIndex = column.getIndex();
  const isPinned = column.getIsPinned();
  const columnWidthNumberValue = table.getAllColumns().sort((columnA, columnB) => columnA.getIndex() - columnB.getIndex()).map((column) => {return convertPxValueToNumber(getWidthIfPinned(isPinned, column.columnDef.meta?.width))});

  const combinedStyle = {
    ...style, 
    ...getFrozenColumnStyle(column.getIsPinned(), column.getIsLastColumn("left"), column.getIsFirstColumn("right"), column.getIndex(), table.getVisibleFlatColumns().length, true, pinningStyle, pinnedWidthOffset(isPinned, columnWidthNumberValue, columnIndex)),
    position: "sticky",
    cursor: !colorId && onClick ? "pointer" : "cursor",
    justifyContent: "flex-start"
  } as CSSProperties

  return (
    <StyledHeadCell style={combinedStyle} key={keyPass} onClick={!colorId ? onClick : undefined}>
      {value}

      {sortArrow && (
        isSorted === "asc" ? 
          <glyphs.Arrow rotate={-90} size={"15px"}/> 
          : isSorted === "desc" ? 
          <glyphs.Arrow rotate={90} size={"15px"}/> 
          : <glyphs.Arrow rotate={90} transparency={0.3} size={"15px"}/> 
      )}
      
    </StyledHeadCell>
  )
}